
import { defineComponent } from 'vue'
import { formatNumberWithLocale, primeVueDate, localeDate } from '@/helpers/commons/localeHelper'
import { luxonFormatDate, formatDateWithLocale } from '@/helpers/commons/localeHelper'
import Checkbox from 'primevue/checkbox'
import KnPivotTableEditableField from './KnPivotTableEditableField.vue'
import Paginator from 'primevue/paginator'
import RegistryDatatableWarningDialog from '@/modules/documentExecution/registry/tables/RegistryDatatableWarningDialog.vue'
import descriptor from '@/modules/documentExecution/registry/tables/RegistryDatatableDescriptor.json'

// // Date format is fixed to MM/DD/YYYY hh:mm:ss for compatibility with Primevue Calendar with Davide Vernassa approval

export default defineComponent({
    name: 'kn-pivot-table',
    components: { Checkbox, KnPivotTableEditableField, Paginator, RegistryDatatableWarningDialog },
    props: {
        columns: [] as any,
        rows: [] as any,
        propConfiguration: { type: Object },
        entity: { type: String },
        id: { type: String },
        pagination: { type: Object },
        comboColumnOptions: { type: Array },
        numberOfRows: { type: Number },
        stopWarningsState: { type: Array }
    },
    emits: ['rowChanged', 'pageChanged', 'dropdownOpened', 'warningChanged'],
    created() {
        this.mapRows()
        this.checkForRowSpan(0, this.mappedRows.length - 1, this.mappedRows, this.columns, 1)
        this.loadPagination()
        this.loadColumnOptions()
        this.loadWarningState()
    },
    watch: {
        rows: {
            handler() {
                this.mapRows()
                this.checkForRowSpan(0, this.mappedRows.length - 1, this.mappedRows, this.columns, 1)
            },
            deep: true
        },
        pagination: {
            handler() {
                this.loadPagination()
                this.first = this.pagination?.start
            },
            deep: true
        },
        comboColumnOptions: {
            handler() {
                this.loadColumnOptions()
            },
            deep: true
        }
    },
    data() {
        return {
            descriptor,
            mappedRows: [] as any,
            configuration: {} as any,
            columnOptions: [] as any[],
            dependentColumns: [] as any[],
            selectedRow: null as any,
            warningVisible: false,
            stopWarnings: [] as any[],
            lazyParams: {} as any,
            first: 0
        }
    },
    computed: {
        getCurrentLocaleDefaultDateFormat() {
            return (column) => (column.isEditable ? column.format || primeVueDate() : localeDate())
        }
    },

    methods: {
        mapRows() {
            this.mappedRows = this.rows.map((row) => {
                let newRow = { id: row.id }
                this.columns.forEach((column) => {
                    newRow[column.field] = { data: row[column.field], rowSpan: 1 }
                })
                return newRow
            })
        },
        checkForRowSpan(fromIndex, toIndex, rows, columns, columnIndex) {
            const column = columns[columnIndex]

            if (column.type !== 'merge') {
                return
            }

            let groupCount = 1
            let startIndex = fromIndex
            for (let i = fromIndex + 1; i <= toIndex; i++) {
                if (rows[i - 1][column.field].data === rows[i][column.field].data) {
                    rows[i][column.field].rowSpan = 0
                    groupCount++
                }
                if (rows[i - 1][column.field].data !== rows[i][column.field].data || i === toIndex) {
                    rows[startIndex][column.field].rowSpan = groupCount
                    if (i - 1 > startIndex && columnIndex < columns.length - 1) {
                        this.checkForRowSpan(startIndex, i === toIndex ? i : i - 1, rows, columns, columnIndex + 1)
                    }
                    startIndex = i
                    groupCount = 1
                }
            }
        },
        loadPagination() {
            this.lazyParams = { ...this.pagination } as any
        },
        onPage(event: any) {
            this.lazyParams = {
                paginationStart: event.first,
                paginationLimit: event.rows,
                paginationEnd: event.first + event.rows,
                size: this.lazyParams.size
            }
            this.$emit('pageChanged', this.lazyParams)
        },
        loadWarningState() {
            this.stopWarnings = this.stopWarningsState as any[]
        },
        getFormattedNumber(number: number, precision?: number, format?: any) {
            return formatNumberWithLocale(number, precision, format)
        },
        setRowEdited(row: any) {
            row.edited = true
            this.$emit('rowChanged', row)
        },
        onDropdownChange(payload: any) {
            const column = payload.column
            const row = payload.row

            row[column.field] = { data: row[column.field].data['column_1'], rowSpan: 1 }
            this.selectedRow = row

            if (column.hasDependencies) {
                this.dependentColumns = [] as any[]
                this.setDependentColumns(column)

                if (!this.stopWarnings[column.field]) {
                    this.dependentColumns.forEach((el: any) => {
                        if (this.selectedRow[el.field]) {
                            this.warningVisible = true
                        }
                    })
                } else {
                    this.clearDependentColumnsValues()
                }
            }

            row.edited = true
            this.$emit('rowChanged', row)
        },
        setDependentColumns(column: any) {
            let tempColumn = column

            if (!tempColumn.hasDependencies) {
                return
            }

            tempColumn.hasDependencies.forEach((el: any) => {
                this.dependentColumns.push(el)
                this.setDependentColumns(el)
            })
        },
        onWarningDialogClose(payload: any) {
            if (payload.stopWarnings) {
                this.stopWarnings[payload.columnField] = true
                this.$emit('warningChanged', this.stopWarnings)
            }

            this.clearDependentColumnsValues()
            this.warningVisible = false
        },
        clearDependentColumnsValues() {
            this.dependentColumns.forEach((el: any) => (this.selectedRow[el.field] = { data: '', rowSpan: 1 }))
            this.selectedRow.edited = true
            this.$emit('rowChanged', this.selectedRow)
        },
        loadColumnOptions() {
            this.columnOptions = this.comboColumnOptions as any[]
        },
        getFormattedDate(date: any, inputFormat?: any, outputFormat?: string) {
            return luxonFormatDate(date, inputFormat, outputFormat)
        },
        getFormattedDateTime(date: any, format?: any, keepNull?: boolean) {
            return formatDateWithLocale(date, format, keepNull)
        }
    }
})
